import React, { useEffect, useState } from "react";
import cn from "classnames";
import { seaErrorMessageMap } from "entities/shared/seaCard.types";
import { ChangeShippingLineRequestFunc, ShipmentModel } from "features/shared/shipmentCard.types";
import { SeaDataLayersManager } from "features/shared/DataLayerManager";
import { authStore } from "entities/Auth";
import { SealineSelect, useSealinesQuery } from "entities/Sealines";
import { isSeaSavedShipmentModel } from "entities/SeaSavedCard";
import { isSeaSearchShipmentModel } from "entities/SeaSearchCard";
import { createSeaSearchQueryParams } from "entities/SeaSearchCard";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { Nullable } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { Button } from "shared/ui/Button";
import { AlertIcon1 } from "shared/ui/Icons";
import { Portal } from "shared/ui/Portal";
import { Preloader } from "shared/ui/Preloader";
import styles from "./ChangeShippingLineModal.module.scss";

import { useChangeShippingLineSearchQuery } from "../../model/useChangeShippingLineSearchQuery";

type ChangeShippingLineModalProps = {
  className?: string;
  closeModal: () => void;
  isShippingLineError: boolean;
  setIsShippingLineError: React.Dispatch<React.SetStateAction<boolean>>;
  openedCard: Nullable<ShipmentModel>;
  setOpenedCardAction: (card: ShipmentModel | null) => {
    type: string;
    payload: ShipmentModel | null;
  };
  updateOpenCardAction: (card: ShipmentModel) => {
    type: string;
    payload: ShipmentModel;
  };
};

export const ChangeShippingLineModal: RFC<ChangeShippingLineModalProps> = ({
  isShippingLineError,
  setIsShippingLineError,
  closeModal,
  setOpenedCardAction,
  updateOpenCardAction,
  openedCard,
  className,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { seaLineOptions } = useSealinesQuery();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);

  const [selectedScacCode, setSelectedScacCode] = useState("");

  const changeShippingLineSearchMeta = useChangeShippingLineSearchQuery(isAuthenticated);

  const changeShippingLineRequest: ChangeShippingLineRequestFunc = (sealine) => {
    if (!openedCard || (!isSeaSavedShipmentModel(openedCard) && !isSeaSearchShipmentModel(openedCard))) return;

    const reqParams = createSeaSearchQueryParams({
      model: {
        reference: openedCard.reference,
        referenceType: openedCard.referenceType,
        carrierScac: isSeaSavedShipmentModel(openedCard) ? openedCard.carrierScac : openedCard.getCarrierScac(),
      },
      requestType: "shippingLine",
      override: { carrier_code: sealine },
    });

    if (!reqParams) return;

    const dlm = new SeaDataLayersManager().searchInitiated();
    changeShippingLineSearchMeta.searchRequest(reqParams, {
      onSuccess: (response) => {
        dlm.setModel(response).handleSearch();
        if (response.isSuccess) {
          dispatch(setOpenedCardAction(response));
          dispatch(updateOpenCardAction(response));
        }
      },
    });
  };

  useEffect(() => {
    if (
      !changeShippingLineSearchMeta.isSearchLoading &&
      changeShippingLineSearchMeta.searchResponse &&
      !changeShippingLineSearchMeta.searchResponse.isSuccess
    ) {
      setIsShippingLineError(true);
    }
  }, [
    changeShippingLineSearchMeta.isSearchLoading,
    changeShippingLineSearchMeta.isSearchFetching,
    changeShippingLineSearchMeta.searchResponse?.status,
  ]);

  // close modal if success
  useEffect(() => {
    if (
      changeShippingLineSearchMeta.isSearchSuccess &&
      changeShippingLineSearchMeta.searchResponse &&
      changeShippingLineSearchMeta.searchResponse.isSuccess
    ) {
      closeModal();
    }
  }, [changeShippingLineSearchMeta.isSearchSuccess, changeShippingLineSearchMeta.searchResponse]);

  if (
    (changeShippingLineSearchMeta.isSearchError || isShippingLineError) &&
    changeShippingLineSearchMeta.searchResponse
  ) {
    return (
      <Portal closePopup={closeModal} className={styles.modal}>
        <div className={styles.changeShippingLineModalError}>
          <div className={styles.closeIcon} onClick={closeModal} />
          <AlertIcon1 className={styles.icon} />
          <div className={styles.errorText}>
            {t(seaErrorMessageMap[changeShippingLineSearchMeta.searchResponse.searchError])}
          </div>
        </div>
      </Portal>
    );
  }

  if (changeShippingLineSearchMeta.isSearchLoading) {
    return (
      <Portal closePopup={closeModal} className={styles.modal}>
        <div className={styles.changeShippingLineModalLoading}>
          <Preloader />
          <div className={styles.loadingText}>{t("Loading data for selected shipping line")}</div>
        </div>
      </Portal>
    );
  }

  return (
    <Portal closePopup={closeModal} className={styles.modal}>
      <div className={cn(styles.content, className)}>
        <div className={styles.title}>{t("Change shipping line")}</div>
        <div className={styles.sealinesList}>
          {seaLineOptions.length > 0 && (
            <SealineSelect value={selectedScacCode} onChange={(value) => setSelectedScacCode(value.value)} />
          )}
        </div>

        <div className={styles.buttonsWrapper}>
          <button type="button" className={styles.button} onClick={closeModal}>
            {t("Cancel")}
          </button>
          {selectedScacCode && (
            <Button
              type="button"
              size="small"
              variant="primary"
              onClick={() =>
                changeShippingLineRequest(selectedScacCode.toLowerCase() === "auto" ? null : selectedScacCode)
              }
              className={styles.button}
            >
              {t("Change")}
            </Button>
          )}
        </div>
      </div>
    </Portal>
  );
};
